export type NativeUpdateStatus = 'none' | 'recommended' | 'required';

export const APP_STORE_URLS = {
  ios: 'https://apps.apple.com/app/id6762815289',
  android: 'https://play.google.com/store/apps/details?id=de.opebo.app',
} as const;

export interface NativeVersionSettings {
  minNativeVersionIos: string | null;
  recommendedNativeVersionIos: string | null;
  minNativeVersionAndroid: string | null;
  recommendedNativeVersionAndroid: string | null;
}

export interface PlatformNativeVersions {
  min: string | null;
  recommended: string | null;
}

/** iOS and Android apps are approved at different times, so each platform has its own versions */
export const selectNativeVersions = (settings: NativeVersionSettings, platform: string): PlatformNativeVersions =>
  platform === 'android'
    ? { min: settings.minNativeVersionAndroid, recommended: settings.recommendedNativeVersionAndroid }
    : { min: settings.minNativeVersionIos, recommended: settings.recommendedNativeVersionIos };

const VERSION_PATTERN = /^(\d+)\.(\d+)\.(\d+)$/;

const parseVersion = (version: string): number[] | null => {
  const match = VERSION_PATTERN.exec(version.trim());
  return match ? match.slice(1).map(Number) : null;
};

/** Negative if a < b, 0 if equal, positive if a > b; null if one of them is not MAJOR.MINOR.PATCH */
export const compareNativeVersions = (a: string, b: string): number | null => {
  const left = parseVersion(a);
  const right = parseVersion(b);
  if (!left || !right) return null;

  for (let i = 0; i < left.length; i++) {
    const diff = left[i]! - right[i]!;
    if (diff !== 0) return diff;
  }
  return 0;
};

const isBelow = (current: string, threshold: string | null): boolean => {
  if (!threshold) return false;
  const comparison = compareNativeVersions(current, threshold);
  return comparison !== null && comparison < 0;
};

/** Unparsable versions never block the app */
export const resolveNativeUpdateStatus = (
  currentVersion: string,
  minNativeVersion: string | null,
  recommendedNativeVersion: string | null,
): NativeUpdateStatus => {
  if (isBelow(currentVersion, minNativeVersion)) return 'required';
  if (isBelow(currentVersion, recommendedNativeVersion)) return 'recommended';
  return 'none';
};
